import { action, computed, createModel, signal } from '@preact/signals-react';
import type { Model, ModelConstructor, ReadonlySignal, Signal } from '@preact/signals-react';
import type { ConcreteDataType } from '@thinkalpha/language-services';
import type { ConcreteIndicator } from '@thinkalpha/platform-ws-client/contracts/dictionary.js';
import type { Logger } from '@thinkalpha/ui-ioc/core/types/Logger';
import type { ColumnTemplate } from '@thinkalpha/ui-types/contracts/column-template';
import { isEqual } from 'es-toolkit';
import type { AggregatedRiskWidgetConfig } from 'src/features/aggregated-risk/config';
import type { GridSettingsNavigationTarget } from 'src/features/grid-settings/models/NavigationModel';
import type { SlugMapDict } from 'src/lib/dictionary/dictionary';
import { StickyFilterModel } from 'src/models/StickyFilterModel';
import type { StickyFilterModelEventBus, StickyFilterState } from 'src/models/StickyFilterModel';
import type { TableModel } from 'src/models/TableModel';
import { getColumnTemplateByIdQuery, getDefaultColumnTemplateQuery } from 'src/queries/columnTemplates';
import { generateWorkableColumnTemplateGridSettings } from 'src/routes/dashboard/widget-container/widget-settings-editor/context';
import type { WidgetHostContainer } from 'src/services/WorkspaceService/Container/Container';
import { ContainerChannelSlice } from 'src/services/WorkspaceService/Container/slices/ContainerChannelSlice';
import type { ContainerChannelSliceEventBus } from 'src/services/WorkspaceService/Container/slices/ContainerChannelSlice';
import { ContainerTableSlice } from 'src/services/WorkspaceService/Container/slices/ContainerTableSlice';
import type { ContainerTableSliceEventBus } from 'src/services/WorkspaceService/Container/slices/ContainerTableSlice';
import { ContainerWidgetTabSlice } from 'src/services/WorkspaceService/Container/slices/ContainerWidgetTabSlice';
import type { ContainerWidgetTabSliceEventBus } from 'src/services/WorkspaceService/Container/slices/ContainerWidgetTabSlice';
import type { WidgetId } from 'src/services/WorkspaceService/selfOwnedWidget';
import type { ReactBindings } from 'src/types/bindings';
import type {
    GridSettingsDialogOpenEvent,
    GridSettingsDialogTargetSignal,
    IsColumnTemplateLibraryOpenSignal,
    IsGridSettingsDialogOpenSignal,
    OpenColumnTemplateLibraryEvent,
    SelectColumnTemplateEvent,
    SelectedColumnTemplateSignal,
    StickyFilterSliceEvents,
    StickyFilterSliceSignals,
    UpdateConcreteTemplateEvent,
} from 'src/types/EventBus';
import { EventBusBuilder, MergedEventBus } from 'src/types/EventBus/EventBus';
import type { EventBus, EventBusEvents, EventBusSignals } from 'src/types/EventBus/EventBus';

export interface AggregatedRiskWidgetState extends StickyFilterState {
    columnTemplate: ColumnTemplate | string | null;
}

export type AggregatedRiskColumnTemplateSignals = SelectedColumnTemplateSignal &
    IsColumnTemplateLibraryOpenSignal &
    IsGridSettingsDialogOpenSignal &
    GridSettingsDialogTargetSignal;

export type AggregatedRiskColumnTemplateEvents = SelectColumnTemplateEvent &
    UpdateConcreteTemplateEvent &
    OpenColumnTemplateLibraryEvent &
    GridSettingsDialogOpenEvent;

export type AggregatedRiskSelfOwnedEventBus = EventBus<
    EventBusSignals<ContainerChannelSliceEventBus> &
        EventBusSignals<ContainerWidgetTabSliceEventBus> &
        EventBusSignals<ContainerTableSliceEventBus> &
        StickyFilterSliceSignals &
        AggregatedRiskColumnTemplateSignals,
    EventBusEvents<ContainerChannelSliceEventBus> &
        EventBusEvents<ContainerWidgetTabSliceEventBus> &
        EventBusEvents<ContainerTableSliceEventBus> &
        StickyFilterSliceEvents &
        AggregatedRiskColumnTemplateEvents
>;

export interface AggregatedRiskSelfOwnedModelInit {
    config: AggregatedRiskWidgetConfig;
    container: WidgetHostContainer;
    logger: Logger;
    persisted: AggregatedRiskWidgetState;
    queryClient: ReactBindings['QueryClient'];
    slugMapService: ReactBindings['SlugMapService'];
    tabId: WidgetId;
    table: TableModel;
}

export interface AggregatedRiskSelfOwnedModelShape {
    readonly dirty: ReadonlySignal<boolean>;
    readonly eventBus: AggregatedRiskSelfOwnedEventBus;
    readonly columnTemplate: ReadonlySignal<ColumnTemplate | null>;
    readonly groupByIndicatorIds: ReadonlySignal<readonly string[]>;
    readonly columnTemplateError: ReadonlySignal<Error | null>;
    toJSON(): AggregatedRiskWidgetState;
    markClean(): void;
}

function asError(error: unknown): Error {
    return error instanceof Error ? error : new Error(String(error));
}

function ensureGroupByColumns({
    columnTemplate,
    config,
    slugMap,
}: {
    columnTemplate: ColumnTemplate;
    config: AggregatedRiskWidgetConfig;
    slugMap: SlugMapDict;
}): { columnTemplate: ColumnTemplate; groupByIndicatorIds: readonly string[] } {
    let columns = columnTemplate.columns;
    const groupByIndicatorIds: string[] = [];

    for (const slug of config.groupBySlugs) {
        const indicator = slugMap[slug];

        if (!indicator) {
            throw new Error(`Missing required group-by indicator for slug ${slug}`);
        }
        if (indicator.disabled) {
            throw new Error(`Required group-by indicator for slug ${slug} is disabled`);
        }

        groupByIndicatorIds.push(indicator.id);
        columns = ensureGroupByColumn(columns, indicator);
    }

    return {
        columnTemplate: {
            ...columnTemplate,
            columns,
        },
        groupByIndicatorIds,
    };
}

function ensureGroupByColumn(
    columns: ColumnTemplate['columns'],
    indicator: ConcreteIndicator,
): ColumnTemplate['columns'] {
    const found = columns.some((column) => {
        const isGroupByColumn =
            column.id === indicator.id || (column.type === 'indicator' && column.indicator.id === indicator.id);

        return isGroupByColumn;
    });

    if (found) {
        return columns;
    }

    return [
        ...columns,
        {
            type: 'indicator',
            indicator,
            id: indicator.id,
            name: indicator.name,
            unit: typeof indicator.unit === 'string' ? indicator.unit : null,
            visible: true,
            dataType: indicator.dataType as ConcreteDataType,
            displaySettings: [],
        },
    ];
}

type ColumnTemplateInput = AggregatedRiskWidgetState['columnTemplate'];
type ColumnTemplateResolutionCause = 'initial' | 'reference-change' | 'concrete-update';

interface ResolvedAggregatedRiskColumnTemplate {
    columnTemplate: ColumnTemplate;
    groupByIndicatorIds: readonly string[];
}

export const AggregatedRiskSelfOwnedModel: ModelConstructor<
    AggregatedRiskSelfOwnedModelShape,
    [AggregatedRiskSelfOwnedModelInit]
> = createModel<AggregatedRiskSelfOwnedModelShape, [AggregatedRiskSelfOwnedModelInit]>(
    (init: AggregatedRiskSelfOwnedModelInit) => {
        const channelSlice = new ContainerChannelSlice({ container: init.container, tabId: init.tabId });
        const widgetTabSlice = new ContainerWidgetTabSlice({
            container: init.container,
            tabId: init.tabId,
            logger: init.logger,
        });
        const tableSlice = new ContainerTableSlice({ table: init.table });
        const stickyFilterModel = new StickyFilterModel({ persisted: init.persisted });

        const persistedColumnTemplate: Signal<ColumnTemplate | string | null> = signal(init.persisted.columnTemplate);
        const cleanColumnTemplate: Signal<ColumnTemplate | string | null> = signal(init.persisted.columnTemplate);
        const resolvedColumnTemplate: Signal<ResolvedAggregatedRiskColumnTemplate | null> = signal(null);
        const columnTemplateError: Signal<Error | null> = signal(null);
        const isGridSettingsDialogOpen = signal(false);
        const gridSettingsDialogTarget = signal<GridSettingsNavigationTarget | null>(null);
        const isColumnTemplateLibraryOpen = signal(false);
        const dirty: ReadonlySignal<boolean> = computed(
            () => !isEqual(persistedColumnTemplate.value, cleanColumnTemplate.value) || stickyFilterModel.dirty.value,
        );
        let resolveRequestId = 0;
        let lastAppliedColumnTemplate: ColumnTemplateInput | undefined;
        let pendingColumnTemplate: ColumnTemplateInput | undefined;

        const openGridSettingsDialog = (payload: {
            isOpen: boolean;
            targetTab?: GridSettingsNavigationTarget['tab'];
            targetColumnId?: string;
        }) => {
            isGridSettingsDialogOpen.value = payload.isOpen;
            gridSettingsDialogTarget.value =
                payload.isOpen && (payload.targetTab !== undefined || payload.targetColumnId !== undefined)
                    ? { tab: payload.targetTab, columnId: payload.targetColumnId }
                    : null;
        };

        const applyResolvedColumnTemplate = action(
            (incomingColumnTemplate: ColumnTemplateInput, resolved: ResolvedAggregatedRiskColumnTemplate): void => {
                resolvedColumnTemplate.value = resolved;
                columnTemplateError.value = null;
                lastAppliedColumnTemplate = incomingColumnTemplate;
                pendingColumnTemplate = undefined;
            },
        );

        const resolveColumnTemplate = async (
            incomingColumnTemplate: ColumnTemplateInput,
            cause: ColumnTemplateResolutionCause,
        ) => {
            const requestId = ++resolveRequestId;
            pendingColumnTemplate = incomingColumnTemplate;
            columnTemplateError.value = null;

            try {
                const columnTemplate =
                    incomingColumnTemplate === null
                        ? await init.queryClient.fetchQuery(getDefaultColumnTemplateQuery(init.config.templateKey))
                        : typeof incomingColumnTemplate === 'string'
                          ? await init.queryClient.fetchQuery(getColumnTemplateByIdQuery(incomingColumnTemplate))
                          : incomingColumnTemplate;

                if (requestId !== resolveRequestId) {
                    return;
                }

                // Preserve the synchronous path for concrete edits once the shared service is ready.
                if (!init.slugMapService.slugMap.value) {
                    await init.slugMapService.initialize();
                }

                if (requestId !== resolveRequestId) {
                    return;
                }

                const slugMap = init.slugMapService.slugMap.value;
                if (!slugMap) {
                    throw new Error('Slug map is unavailable after initialization');
                }

                const ensured = ensureGroupByColumns({
                    columnTemplate,
                    config: init.config,
                    slugMap,
                });

                if (requestId !== resolveRequestId) {
                    return;
                }

                applyResolvedColumnTemplate(incomingColumnTemplate, {
                    columnTemplate: generateWorkableColumnTemplateGridSettings(ensured.columnTemplate),
                    groupByIndicatorIds: ensured.groupByIndicatorIds,
                });
            } catch (error) {
                if (requestId !== resolveRequestId) {
                    return;
                }

                const normalizedError = asError(error);
                init.logger.error('Failed to resolve aggregated risk column template', {
                    error: normalizedError,
                    incomingColumnTemplate,
                    cause,
                    tableKind: init.config.tableKind,
                    templateKey: init.config.templateKey,
                });
                pendingColumnTemplate = undefined;
                if (cause === 'initial' || resolvedColumnTemplate.value === null) {
                    resolvedColumnTemplate.value = null;
                    columnTemplateError.value = normalizedError;
                }
            }
        };

        const setColumnTemplate = (
            columnTemplate: ColumnTemplateInput,
            cause: Exclude<ColumnTemplateResolutionCause, 'initial'>,
        ) => {
            if (lastAppliedColumnTemplate !== undefined && isEqual(lastAppliedColumnTemplate, columnTemplate)) {
                if (!isEqual(persistedColumnTemplate.value, columnTemplate)) {
                    resolveRequestId += 1;
                    pendingColumnTemplate = undefined;
                    persistedColumnTemplate.value = columnTemplate;
                    columnTemplateError.value = null;
                }
                return;
            }
            if (pendingColumnTemplate !== undefined && isEqual(pendingColumnTemplate, columnTemplate)) {
                return;
            }

            persistedColumnTemplate.value = columnTemplate;
            void resolveColumnTemplate(columnTemplate, cause);
        };

        const columnTemplateEventBus = EventBusBuilder.create()
            .addEvent('widget::updateConcreteTemplate', (payload) => {
                setColumnTemplate(payload.columnTemplate, 'concrete-update');
            })
            .addEvent('widget::selectColumnTemplate', (payload) => {
                setColumnTemplate(payload.templateId, 'reference-change');
            })
            .addEvent('widget::onColumnTemplateLibraryOpen', (payload) => {
                isColumnTemplateLibraryOpen.value = payload.isOpen;
            })
            .addEvent('widget::openGridSettingsDialog', openGridSettingsDialog)
            .addSignal('widget::selectedColumnTemplate', () => persistedColumnTemplate)
            .addSignal('widget::isColumnTemplateLibraryOpen', () => isColumnTemplateLibraryOpen)
            .addSignal('widget::isGridSettingsDialogOpen', () => isGridSettingsDialogOpen)
            .addSignal('widget::gridSettingsDialogTarget', () => gridSettingsDialogTarget)
            .build();

        const eventBus: AggregatedRiskSelfOwnedEventBus = MergedEventBus.from<ContainerChannelSliceEventBus>(
            channelSlice.eventBus,
        )
            .merge<ContainerWidgetTabSliceEventBus>(widgetTabSlice.eventBus)
            .merge<ContainerTableSliceEventBus>(tableSlice.eventBus)
            .merge<StickyFilterModelEventBus>(stickyFilterModel.eventBus)
            .merge(columnTemplateEventBus);

        void resolveColumnTemplate(persistedColumnTemplate.value, 'initial');

        return {
            dirty,
            eventBus,
            columnTemplate: computed(() => resolvedColumnTemplate.value?.columnTemplate ?? null),
            groupByIndicatorIds: computed(() => resolvedColumnTemplate.value?.groupByIndicatorIds ?? []),
            columnTemplateError: columnTemplateError as ReadonlySignal<Error | null>,
            toJSON(): AggregatedRiskWidgetState {
                return {
                    ...init.persisted,
                    columnTemplate: persistedColumnTemplate.value,
                    ...stickyFilterModel.toJSON(),
                };
            },
            markClean(): void {
                cleanColumnTemplate.value = persistedColumnTemplate.value;
                stickyFilterModel.markClean();
            },
        };
    },
);

export type AggregatedRiskSelfOwnedModel = Model<AggregatedRiskSelfOwnedModelShape>;
